import React, { useEffect, useRef } from "react";
import { useLocation, useNavigate } from "react-router-dom";
import { useTranslation } from "react-i18next";
import { extractLanguageFromPath, LanguageCode, SUPPORTED_LANGUAGES } from "../utils/languageRouting";
import LocationSeoManager from "./LocationSeoManager";

/**
 * Language Router Component
 * Automatically intercepts all navigation and adds language prefix
 * This ensures ALL links maintain the language code without modifying individual components
 */
const LanguageRouter: React.FC<{ children: React.ReactNode }> = ({ children }) => {
  const location = useLocation();
  const navigate = useNavigate();
  const { i18n } = useTranslation();
  const isNavigatingRef = useRef(false);

  useEffect(() => {
    // Skip if we're in the middle of a navigation we triggered
    if (isNavigatingRef.current) {
      isNavigatingRef.current = false;
      return;
    }

    const currentPath = location.pathname;
    const { language: urlLanguage, cleanPath } = extractLanguageFromPath(currentPath);
    // Normalize language code (en-US, en-GB -> en)
    const currentLanguage = (i18n.language.split("-")[0]) as LanguageCode;

    console.log("🔍 LanguageRouter:", { currentPath, urlLanguage, cleanPath, currentLanguage, rawLanguage: i18n.language });

    // Scenario 1: URL has language, i18n matches - do nothing (IMPORTANT!)
    if (urlLanguage && urlLanguage === currentLanguage) {
      // Everything is in sync, no action needed
      console.log("✅ URL and i18n in sync, no action");
      return;
    }

    // Scenario 2: No language in URL, i18n is English - do nothing (default state)
    if (!urlLanguage && currentLanguage === "en") {
      // Normal English state, no action needed
      console.log("✅ English with no prefix, correct state");
      return;
    }

    // Scenario 3: URL has language, but i18n is different
    if (urlLanguage && urlLanguage !== currentLanguage) {
      // If i18n is English and URL has language, remove language from URL
      if (currentLanguage === "en") {
        console.log("🔄 Removing language prefix from URL");
        isNavigatingRef.current = true;
        navigate(cleanPath, { replace: true });
        return;
      }
      // Otherwise, update i18n to match URL (don't navigate)
      console.log("🔄 Updating i18n to match URL language:", urlLanguage);
      i18n.changeLanguage(urlLanguage);
      return;
    }

    // Scenario 4: No language in URL, but non-English language is selected
    if (!urlLanguage && currentLanguage !== "en") {
      // Ensure cleanPath doesn't already contain the language prefix
      // This prevents double-appending like /it/it/professionals
      const segments = cleanPath.split("/").filter(Boolean);
      if (segments[0] === currentLanguage) {
        console.log("⚠️ Clean path already starts with language, skipping");
        return;
      }
      
      // Build the new path with language prefix
      // For root path ("/"), just use the language code (e.g., "/it")
      // For other paths, append after language (e.g., "/it/professionals")
      const newPath = cleanPath === "/" ? `/${currentLanguage}` : `/${currentLanguage}${cleanPath}`;
      
      console.log("🔄 Adding language prefix to URL:", newPath);
      
      // Only navigate if not already at the target path
      if (newPath !== currentPath) {
        isNavigatingRef.current = true;
        navigate(newPath, { replace: true });
      }
      return;
    }
  }, [location.pathname, i18n.language, navigate, i18n]);

  return <LocationSeoManager>{children}</LocationSeoManager>;
};

export default LanguageRouter;

